import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Search, Plus, Trash2, Phone, MessageCircle, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useVehicles } from "@/hooks/usePublicData";

const STATUSES = ["new", "pending", "confirmed", "completed", "cancelled"];
const STATUS_COLORS = {
  new: "bg-blue-100 text-blue-700", pending: "bg-amber-100 text-amber-700",
  confirmed: "bg-green-100 text-green-700", completed: "bg-slate-100 text-slate-600", cancelled: "bg-red-100 text-red-700",
};

export default function BookingsAdmin() {
  const qc = useQueryClient();
  const [status, setStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [modal, setModal] = useState(null); // 'add' | booking obj

  const { data: bookings = [], isLoading } = useQuery({
    queryKey: ["admin-bookings", status, search],
    queryFn: () => api.get(`/admin/bookings`, { params: { status, search } }).then((r) => r.data),
  });

  const refresh = () => { qc.invalidateQueries({ queryKey: ["admin-bookings"] }); qc.invalidateQueries({ queryKey: ["admin-stats"] }); };

  const setStatusMut = useMutation({
    mutationFn: ({ id, status }) => api.put(`/admin/bookings/${id}`, { status }),
    onSuccess: () => { refresh(); toast.success("Status updated"); },
  });
  const del = useMutation({
    mutationFn: (id) => api.delete(`/admin/bookings/${id}`),
    onSuccess: () => { refresh(); toast.success("Booking deleted"); },
  });

  return (
    <div data-testid="admin-bookings">
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="text-2xl font-heading font-bold text-navy-heading">Bookings</h1><p className="text-slate-500 text-sm">Manage all booking enquiries.</p></div>
        <button onClick={() => setModal("add")} className="inline-flex items-center gap-2 bg-brand text-white font-semibold px-4 py-2.5 rounded-xl text-sm" data-testid="add-booking-btn"><Plus className="w-4 h-4" /> Add Booking</button>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, phone, location..." className="w-full rounded-xl border border-slate-200 pl-10 pr-4 py-2.5 text-sm outline-none focus:border-brand" data-testid="booking-search" />
        </div>
        <div className="flex gap-2 flex-wrap">
          {["all", ...STATUSES].map((s) => (
            <button key={s} onClick={() => setStatus(s)} className={`px-3.5 py-2 rounded-xl text-xs font-semibold capitalize ${status === s ? "bg-navy text-white" : "bg-white border border-slate-200 text-slate-600"}`}>{s}</button>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-left">
            <tr><th className="px-4 py-3 font-medium">Customer</th><th className="px-4 py-3 font-medium">Trip</th><th className="px-4 py-3 font-medium">Route / Date</th><th className="px-4 py-3 font-medium">Status</th><th className="px-4 py-3 font-medium">Actions</th></tr>
          </thead>
          <tbody>
            {isLoading ? <tr><td colSpan={5} className="p-10 text-center text-slate-400">Loading…</td></tr> :
              bookings.length === 0 ? <tr><td colSpan={5} className="p-10 text-center text-slate-400">No bookings found.</td></tr> :
              bookings.map((b) => (
                <tr key={b.id} className="border-t border-slate-100 align-top" data-testid={`booking-row-${b.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-navy-heading">{b.full_name}</div>
                    <div className="text-slate-500 text-xs flex items-center gap-2 mt-1">
                      <a href={`tel:${b.phone}`} className="hover:text-brand flex items-center gap-1"><Phone className="w-3 h-3" /> {b.phone}</a>
                      <a href={`https://wa.me/91${(b.phone||"").replace(/\D/g,'').slice(-10)}`} target="_blank" rel="noreferrer" className="text-whatsapp"><MessageCircle className="w-3.5 h-3.5" /></a>
                    </div>
                    {b.passengers && <div className="text-xs text-slate-400 mt-1">{b.passengers} pax · {b.taxi_type}</div>}
                  </td>
                  <td className="px-4 py-3 text-slate-600">{b.trip_type || "—"}</td>
                  <td className="px-4 py-3 text-slate-600">
                    <div>{b.pickup_location || "—"} → {b.drop_location || "—"}</div>
                    <div className="text-xs text-slate-400 mt-1">{b.travel_date} {b.pickup_time}</div>
                    {b.message && <div className="text-xs text-slate-400 mt-1 max-w-xs truncate">"{b.message}"</div>}
                  </td>
                  <td className="px-4 py-3">
                    <select value={b.status} onChange={(e) => setStatusMut.mutate({ id: b.id, status: e.target.value })}
                      className={`text-xs font-semibold rounded-full px-2.5 py-1 border-0 outline-none capitalize ${STATUS_COLORS[b.status]}`} data-testid={`booking-status-${b.id}`}>
                      {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                  <td className="px-4 py-3">
                    <button onClick={() => { if (window.confirm("Delete this booking?")) del.mutate(b.id); }} className="text-red-500 hover:bg-red-50 p-2 rounded-lg" data-testid={`delete-booking-${b.id}`}><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {modal === "add" && <AddBookingModal onClose={() => setModal(null)} onSaved={() => { setModal(null); refresh(); }} />}
    </div>
  );
}

function AddBookingModal({ onClose, onSaved }) {
  const { data: vehicles = [] } = useVehicles();
  const taxiOptions = vehicles.length ? vehicles.map((v) => v.name) : ["Sedan", "SUV", "Premium SUV", "Tempo Traveller"];
  const [form, setForm] = useState({ full_name: "", phone: "", pickup_location: "", drop_location: "", travel_date: "", pickup_time: "", passengers: "", taxi_type: "", trip_type: "One Way", message: "" });
  const [loading, setLoading] = useState(false);
  useEffect(() => { if (!form.taxi_type && taxiOptions.length) setForm((f) => ({ ...f, taxi_type: taxiOptions[0] })); }, [vehicles]); // eslint-disable-line
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const save = async () => {
    if (!form.full_name || !form.phone) return toast.error("Name and phone required");
    setLoading(true);
    try { await api.post("/admin/bookings", form); toast.success("Booking added"); onSaved(); }
    catch { toast.error("Failed to add"); } finally { setLoading(false); }
  };
  const inp = "w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-brand";
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-2xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4"><h2 className="font-heading font-bold text-navy-heading text-lg">Add Booking</h2><button onClick={onClose}><X className="w-5 h-5" /></button></div>
        <div className="grid grid-cols-2 gap-3">
          <input className={inp} placeholder="Full name *" value={form.full_name} onChange={set("full_name")} />
          <input className={inp} placeholder="Phone *" value={form.phone} onChange={set("phone")} />
          <input className={inp} placeholder="Pickup" value={form.pickup_location} onChange={set("pickup_location")} />
          <input className={inp} placeholder="Drop" value={form.drop_location} onChange={set("drop_location")} />
          <input type="date" className={inp} value={form.travel_date} onChange={set("travel_date")} />
          <input type="time" className={inp} value={form.pickup_time} onChange={set("pickup_time")} />
          <input className={inp} placeholder="Passengers" value={form.passengers} onChange={set("passengers")} />
          <select className={inp} value={form.taxi_type} onChange={set("taxi_type")}>{taxiOptions.map(t=><option key={t}>{t}</option>)}</select>
          <select className={inp} value={form.trip_type} onChange={set("trip_type")}>{["One Way","Round Trip","Local","Airport Transfer","Outstation"].map(t=><option key={t}>{t}</option>)}</select>
        </div>
        <textarea className={`${inp} mt-3`} rows={2} placeholder="Message" value={form.message} onChange={set("message")} />
        <button onClick={save} disabled={loading} className="mt-4 w-full flex items-center justify-center gap-2 bg-brand text-white font-semibold py-3 rounded-xl">{loading && <Loader2 className="w-4 h-4 animate-spin" />} Save Booking</button>
      </div>
    </div>
  );
}
